import { useEffect, useState } from 'react';
import { useNavigate, Outlet, Link, useLocation, useOutletContext } from 'react-router-dom';
import { Camera, Car, KeyRound, LogOut, SlidersHorizontal, Users } from 'lucide-react';
import type { Me } from '@transpordiamet/shared';
import { adminAPI } from '../../lib/admin-api';
import { useAuth } from '../../lib/auth';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
} from '../ui/sidebar';
import { TooltipProvider } from '../ui/tooltip';

const NAV_ITEMS = [
  { to: '/admin/categories', label: 'Sõidukikategooriad', icon: Car },
  { to: '/admin/configurations', label: 'Konfiguratsioonid', icon: SlidersHorizontal },
  { to: '/admin/sessions', label: 'Sessioonid', icon: Camera },
  { to: '/admin/users', label: 'Kasutajad', icon: Users, adminOnly: true },
  { to: '/admin/tokens', label: 'API võtmed', icon: KeyRound, adminOnly: true },
];

/**
 * The logged-in user, for hiding what the API would refuse. Undefined while loading
 * (or when a page renders outside the layout); treat that as "allow" — the API enforces.
 */
export function useMe(): Me | undefined {
  return useOutletContext<Me | undefined>();
}

export function AdminLayout() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [me, setMe] = useState<Me>();

  useEffect(() => {
    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate('/admin', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) adminAPI.getMe().then(setMe, () => {});
  }, [isAuthenticated]);

  const handleLogout = async () => {
    // Revoke the token server-side before clearing it: the request interceptor reads it asynchronously.
    // Errors (offline, already expired) must not block the local logout.
    await adminAPI.logout().catch(() => {});
    logout();
    navigate('/admin', { replace: true });
  };

  // Don't render protected content if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <TooltipProvider>
      <SidebarProvider>
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <div className="px-2 py-1.5 text-lg font-semibold truncate group-data-[collapsible=icon]:hidden">
              Fotosüsteem
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu>
                  {NAV_ITEMS.filter((item) => !item.adminOnly || me?.role === 'admin').map(
                    ({ to, label, icon: Icon }) => (
                      <SidebarMenuItem key={to}>
                        {/* startsWith keeps the item active on its detail pages */}
                        <SidebarMenuButton render={<Link to={to} />} isActive={pathname.startsWith(to)} tooltip={label}>
                          <Icon />
                          <span>{label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )
                  )}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton onClick={handleLogout} tooltip="Logi välja">
                  <LogOut />
                  <span>Logi välja</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>

        <SidebarInset className="min-w-0">
          {/* sidebar-07 content layout; its header is dropped, so p-4 instead of p-4 pt-0 */}
          <main className="flex flex-1 flex-col gap-4 p-4">
            {/* Desktop toggles via the rail or Cmd/Ctrl+B; on mobile the sidebar is off-canvas and needs this */}
            <SidebarTrigger className="-ml-1 md:hidden" />
            <Outlet context={me} />
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  );
}
